import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Plakietka — krótki stan przy elemencie.
 *
 * Warianty stanu (`success`, `warning`, `destructive`) są przezroczystymi
 * tłami z kolorowym obrysem, nie pełnymi wypełnieniami: na ciemnym tle pełny
 * kolor krzyczy głośniej niż treść, którą opisuje.
 */
const badgeVariants = cva(
  cn(
    "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden",
    "rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
    "[&>svg]:size-3 [&>svg]:pointer-events-none",
  ),
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary/20 text-primary-foreground",
        secondary: "border-border bg-secondary text-secondary-foreground",
        accent: "border-accent/40 bg-accent/15 text-accent",
        success: "border-success/40 bg-success/15 text-success",
        warning: "border-warning/40 bg-warning/15 text-warning",
        destructive: "border-destructive/40 bg-destructive/15 text-destructive",
        outline: "border-border text-muted-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.ComponentProps<"span">,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

export function Badge({ className, variant, asChild = false, ...props }: BadgeProps) {
  const Component = asChild ? Slot : "span";
  return (
    <Component data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { badgeVariants };
